import React, { useEffect, useMemo, useState } from 'react';
import { ScrollView, Text, useWindowDimensions, View } from 'react-native';
import { StyleSheet } from 'react-native-unistyles';
import Toast from 'react-native-toast-message';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { Query } from '@/utils/react-query/Queries';
import Input from '@/components/General/inputs/Input';
import ToggleRow from '@/components/General/inputs/ToggleRow';
import ToggleInput from '@/components/General/inputs/ToggleInput';
import SelectInput from '@/components/General/inputs/SelectInput';
import Checkbox from '@/components/General/inputs/Checkbox';
import SheetSelector from '@/components/General/inputs/SheetSelector';
import SegmentedTabs from '@/components/General/SegmentedTabs';
import SolidButton from '@/components/General/SolidButton';
import ProfileViewCard from '@/components/ProfileViews/ProfileViewCard';
import useOrganizationEntitlements from '@/utils/hooks/useOrganizationEntitlements';
import { isFreeOrg } from '@/utils/organizationEntitlements';

/**
 * Profile view builder — left column holds the name + grouped field picker,
 * right column (or the Preview tab on narrow screens) renders a live
 * ProfileViewCard fed by get_profile_view_card against a preview person.
 */

const RANGE_OPTIONS = [
    { label: 'Last 30 days', value: '30d' },
    { label: 'Last 90 days', value: '90d' },
    { label: 'Last 365 days', value: '365d' },
    { label: 'This year', value: 'year' },
    { label: 'All time', value: 'all' },
];

const BASIC_FIELDS = [
    { label: 'Date of Birth', value: 'dateOfBirth' },
    { label: 'Gender', value: 'gender' },
];

const CONTACT_FIELDS = [
    { label: 'Email', value: 'email' },
    { label: 'Phone Numbers', value: 'phones' },
    { label: 'Address', value: 'address' },
];

const DEFAULT_CONFIG = {
    avatar: true,
    basic: { dateOfBirth: false, gender: false },
    contact: { email: false, phones: false, address: false },
    customFieldIDs: [],
    roleIDs: [],
    trainingModuleIDs: [],
    smallGroups: false,
    family: false,
};

const normalizeConfig = (config) => ({
    ...DEFAULT_CONFIG,
    ...(config || {}),
    basic: { ...DEFAULT_CONFIG.basic, ...(config?.basic || {}) },
    contact: { ...DEFAULT_CONFIG.contact, ...(config?.contact || {}) },
    customFieldIDs: config?.customFieldIDs || [],
    roleIDs: config?.roleIDs || [],
    trainingModuleIDs: config?.trainingModuleIDs || [],
});

// attendance/tasks keys are only present while enabled (section keys are
// omitted from config — and from the RPC payload — when not configured).
const buildConfig = (config) => {
    const out = { ...config };
    if (!out.attendance) delete out.attendance;
    if (!out.tasks) delete out.tasks;
    return out;
};

const ProfileViewBuilder = ({ view = null, saving = false, onSubmit, onCancel }) => {
    const { width } = useWindowDimensions();
    const insets = useSafeAreaInsets();
    const isWideScreen = width >= 900;
    const isEditing = !!view?.id;

    const { data: loggedInUser } = Query.people.loggedInUser();
    const { entitlements } = useOrganizationEntitlements();
    const isFree = isFreeOrg(entitlements);
    const canViewCustomFields = !isFree && loggedInUser?.can?.('View Custom Fields');

    const { data: customFieldSections } = Query.personCustomFieldSections.all({ options: { enabled: !!canViewCustomFields } });
    const { data: roles } = Query.roles.all({ user: loggedInUser });
    const { data: trainingModules } = Query.trainingModules.all({});

    const [name, setName] = useState(view?.name || '');
    const [config, setConfig] = useState(() => normalizeConfig(view?.config));
    const [previewPerson, setPreviewPerson] = useState(null);
    const [mobileTab, setMobileTab] = useState('edit');

    // Debounce the draft config so rapid toggle sequences collapse into one
    // preview RPC call (same local-debounce idiom as SearchModal).
    const [previewConfig, setPreviewConfig] = useState(config);
    useEffect(() => {
        const timeout = setTimeout(() => setPreviewConfig(config), 300);
        return () => clearTimeout(timeout);
    }, [config]);

    const previewPersonID = previewPerson?.id || loggedInUser?.id;
    // The draft config is always sent — the RPC renders it directly (skipping
    // the saved-view lookup), so unsaved changes preview live on create AND edit.
    const { data: cardData } = Query.profileViews.card({
        viewID: view?.id,
        personID: previewPersonID,
        config: previewConfig,
    });

    const previewPayload = useMemo(() => ({
        view: { ...(cardData?.view || {}), name: name || cardData?.view?.name, config },
        person: cardData?.person || previewPerson || loggedInUser,
        sections: cardData?.sections || {},
    }), [cardData, name, config, previewPerson, loggedInUser]);

    // SheetSelector values are objects; config stores ids only.
    const selectedRoles = useMemo(
        () => (roles || []).filter(r => config.roleIDs.includes(r.id)),
        [roles, config.roleIDs]
    );
    const trainingModulesFlat = useMemo(
        () => (trainingModules?.pages?.flatMap(p => p.data) || []),
        [trainingModules]
    );
    const selectableModules = useMemo(
        () => trainingModulesFlat.filter(m => m?.status !== 'ARCHIVED'),
        [trainingModulesFlat]
    );
    const selectedModules = useMemo(
        () => selectableModules.filter(m => config.trainingModuleIDs.includes(m.id)),
        [selectableModules, config.trainingModuleIDs]
    );
    // SheetSelector's flat path reads query.data as an array — wrap the
    // flattened infinite-query result so the training picker works.
    const trainingQueryResult = useMemo(
        () => ({ data: selectableModules, isFetching: false }),
        [selectableModules]
    );
    const patchConfig = (patch) => setConfig(prev => ({ ...prev, ...patch }));
    const setBasicField = (field, on) => setConfig(prev => ({ ...prev, basic: { ...prev.basic, [field]: on } }));
    const setContactField = (field, on) => setConfig(prev => ({ ...prev, contact: { ...prev.contact, [field]: on } }));
    const setCustomField = (fieldID, on) => setConfig(prev => ({
        ...prev,
        customFieldIDs: on ? [...prev.customFieldIDs, fieldID] : prev.customFieldIDs.filter(id => id !== fieldID),
    }));
    const toggleAttendance = (on) => patchConfig({ attendance: on ? { range: '30d' } : undefined });
    const toggleTasks = (on) => patchConfig({ tasks: on ? { range: '90d' } : undefined });

    const handleSave = () => {
        if (!name.trim()) {
            Toast.show({ type: 'error', text2: 'Enter a name for the view' });
            return;
        }
        onSubmit?.({ name: name.trim(), config: buildConfig(config) });
    };

    const selectedBasicFields = BASIC_FIELDS.filter(f => config.basic?.[f.value]).map(f => f.value);
    const selectedContactFields = CONTACT_FIELDS.filter(f => config.contact?.[f.value]).map(f => f.value);

    const controls = (
        <ScrollView
            style={styles.controlsScroll}
            contentContainerStyle={styles.controlsContent}
            showsVerticalScrollIndicator={false}
            keyboardShouldPersistTaps="handled"
        >
            <View style={styles.group}>
                <Input
                    title="Name"
                    testID="Input-ViewName"
                    placeholder="e.g. Visitor Follow-up"
                    value={name}
                    onUpdate={setName}
                    required
                    description="Shown to anyone who uses this profile view."
                />
                <ToggleRow
                    label="Show Avatar"
                    value={config.avatar !== false}
                    onValueChange={(on) => patchConfig({ avatar: on })}
                    testID="ProfileView-Toggle-Avatar"
                />
            </View>

            <Text style={styles.groupLabel}>Profile Fields</Text>
            <View style={styles.group}>
                <ToggleInput
                    title="Basic Info"
                    items={BASIC_FIELDS}
                    selectedValues={selectedBasicFields}
                    onItemChanged={setBasicField}
                    itemsPerRow={2}
                    testID="ProfileView-Basic"
                />
                <ToggleInput
                    title="Contact Info"
                    items={CONTACT_FIELDS}
                    selectedValues={selectedContactFields}
                    onItemChanged={setContactField}
                    itemsPerRow={3}
                    testID="ProfileView-Contact"
                />
            </View>

            {canViewCustomFields && (customFieldSections || []).length > 0 && (
                <>
                    <Text style={styles.groupLabel}>Custom Fields</Text>
                    <View style={styles.group}>
                        {(customFieldSections || []).map(section => (
                            (section.CustomFields || []).length > 0 && (
                                <View key={section.id} style={styles.customFieldSection}>
                                    <Text style={styles.customFieldSectionName}>{section.name}</Text>
                                    {(section.CustomFields || []).map(field => (
                                        <View key={field.id} style={styles.customFieldRow}>
                                            <Checkbox
                                                label={field.name}
                                                checked={config.customFieldIDs.includes(field.id)}
                                                onChange={(on) => setCustomField(field.id, on)}
                                                testID={`ProfileView-CustomField-${field.id}`}
                                            />
                                        </View>
                                    ))}
                                </View>
                            )
                        ))}
                    </View>
                </>
            )}

            <Text style={styles.groupLabel}>Activity</Text>
            <View style={styles.group}>
                <ToggleRow
                    label="Attendance"
                    description="Times the person has attended."
                    value={!!config.attendance}
                    onValueChange={toggleAttendance}
                    testID="ProfileView-Toggle-Attendance"
                />
                {!!config.attendance && (
                    <SelectInput
                        title="Attendance Range"
                        value={config.attendance?.range || '30d'}
                        onUpdate={(range) => patchConfig({ attendance: { range } })}
                        options={RANGE_OPTIONS}
                        hideClearButton
                    />
                )}
                <ToggleRow
                    label="Tasks"
                    description="Assigned vs. completed tasks."
                    value={!!config.tasks}
                    onValueChange={toggleTasks}
                    testID="ProfileView-Toggle-Tasks"
                />
                {!!config.tasks && (
                    <SelectInput
                        title="Task Range"
                        value={config.tasks?.range || '90d'}
                        onUpdate={(range) => patchConfig({ tasks: { range } })}
                        options={RANGE_OPTIONS}
                        hideClearButton
                    />
                )}
            </View>

            <Text style={styles.groupLabel}>Roles & Training</Text>
            <View style={styles.group}>
                <SheetSelector
                    type="roles"
                    title="Roles"
                    multiple
                    isNotOnSurface
                    value={selectedRoles}
                    onChange={(items) => patchConfig({ roleIDs: (items || []).map(r => r.id) })}
                    placeholder="Select roles to check"
                />
                <SheetSelector
                    type="trainingModules"
                    title="Training Modules"
                    multiple
                    isNotOnSurface
                    queryResult={trainingQueryResult}
                    value={selectedModules}
                    onChange={(items) => patchConfig({ trainingModuleIDs: (items || []).map(m => m.id) })}
                    placeholder="Select training modules"
                />
            </View>

            <Text style={styles.groupLabel}>Groups & Family</Text>
            <View style={styles.group}>
                <ToggleRow
                    label="Small Groups"
                    description="Groups this person belongs to."
                    value={!!config.smallGroups}
                    onValueChange={(on) => patchConfig({ smallGroups: on })}
                    testID="ProfileView-Toggle-SmallGroups"
                />
                <ToggleRow
                    label="Family"
                    description="Household family members."
                    value={!!config.family}
                    onValueChange={(on) => patchConfig({ family: on })}
                    testID="ProfileView-Toggle-Family"
                />
            </View>
        </ScrollView>
    );

    const preview = (
        <ScrollView
            style={styles.previewScroll}
            contentContainerStyle={styles.previewContent}
            showsVerticalScrollIndicator={false}
            keyboardShouldPersistTaps="handled"
        >
            <View style={styles.previewInner}>
                <SheetSelector
                    type="people"
                    title="Preview Person"
                    placeholder="Select a person to preview"
                    value={previewPerson || loggedInUser}
                    onChange={setPreviewPerson}
                    isNotOnSurface
                />
                <ProfileViewCard data={previewPayload} testID="ProfileViewPreviewCard" />
            </View>
        </ScrollView>
    );

    const footer = (
        // 80 = translucent tab bar height convention used app-wide; insets
        // cover the home-indicator on mobile.
        <View style={[styles.footer, !isWideScreen && { paddingBottom: 80 + insets.bottom }]}>
            <SolidButton
                testID="profile-view-cancel"
                variant="outlined"
                type="textSecondary"
                text="Cancel"
                onPress={onCancel}
            />
            <SolidButton
                testID="profile-view-save"
                text={isEditing ? 'Save Changes' : 'Create View'}
                processing={saving}
                onPress={handleSave}
            />
        </View>
    );

    if (isWideScreen) {
        return (
            <View style={styles.container}>
                <View style={styles.twoColumn}>
                    {/* Preview left, editor right — matches the app's
                        edit-drawer-on-right convention. */}
                    <View style={styles.previewPanel}>{preview}</View>
                    <View style={styles.controlsPanel}>
                        {controls}
                        {footer}
                    </View>
                </View>
            </View>
        );
    }

    return (
        <View style={styles.container}>
            <View style={styles.mobileTabs}>
                <SegmentedTabs
                    tabs={[{ key: 'edit', label: 'Edit' }, { key: 'preview', label: 'Preview' }]}
                    value={mobileTab}
                    onChange={setMobileTab}
                    testIDPrefix="profile-view-tab"
                />
            </View>
            {mobileTab === 'edit' ? (
                <View style={styles.mobileEditPanel}>
                    {controls}
                    {footer}
                </View>
            ) : preview}
        </View>
    );
};

const styles = StyleSheet.create(({ colors, fonts, shadows, spacing, typography, roundness }) => ({
    container: {
        flex: 1,
        backgroundColor: 'transparent',
    },
    twoColumn: {
        flex: 1,
        flexDirection: 'row',
    },
    previewPanel: {
        flex: 2,
        borderRightWidth: StyleSheet.hairlineWidth,
        borderRightColor: colors.borderSubtle,
    },
    controlsPanel: {
        flex: 1,
        maxWidth: 480,
    },
    controlsScroll: {
        flex: 1,
    },
    controlsContent: {
        padding: spacing.md,
        paddingBottom: spacing.xl,
        gap: spacing.xs,
    },
    group: {
        backgroundColor: colors.surface,
        borderRadius: roundness.md,
        paddingHorizontal: spacing.sm + 2,
        paddingVertical: spacing.sm,
        boxShadow: shadows.card,
        marginBottom: spacing.md,
        gap: spacing.xs,
    },
    groupLabel: {
        fontSize: typography.micro,
        fontFamily: fonts.heading,
        color: colors.textSecondary,
        textTransform: 'uppercase',
        letterSpacing: 0.4,
        paddingLeft: spacing.xs,
        paddingBottom: spacing.xs - 2,
        marginTop: spacing.xs,
    },
    customFieldSection: {
        gap: spacing.xs,
        paddingBottom: spacing.xs,
    },
    customFieldSectionName: {
        fontSize: typography.micro,
        fontFamily: fonts.heading,
        color: colors.textTertiary,
        textTransform: 'uppercase',
        letterSpacing: 0.4,
        paddingTop: spacing.xs,
    },
    customFieldRow: {
        paddingVertical: 2,
    },
    previewScroll: {
        flex: 1,
    },
    previewContent: {
        padding: spacing.md,
        alignItems: 'center',
    },
    previewInner: {
        width: '100%',
        maxWidth: 420,
        gap: spacing.sm,
    },
    footer: {
        flexDirection: 'row',
        justifyContent: 'flex-end',
        gap: spacing.sm,
        padding: spacing.md,
        borderTopWidth: StyleSheet.hairlineWidth,
        borderTopColor: colors.borderSubtle,
        backgroundColor: colors.surface,
    },
    mobileTabs: {
        padding: spacing.sm,
        paddingHorizontal: spacing.md,
    },
    mobileEditPanel: {
        flex: 1,
    },
}));

export default ProfileViewBuilder;
